import * as yaml from 'js-yaml';
import type { KubernetesManifest } from '../types.js';

export function parseYaml(yamlString: string): KubernetesManifest[] {
  try {
    // Парсим YAML, поддерживаем многодокументные файлы
    const documents = yaml.loadAll(yamlString) as any[];
    
    // Фильтруем null и undefined документы
    const manifests = documents.filter(doc => doc && typeof doc === 'object') as KubernetesManifest[];
    
    // Валидируем базовую структуру Kubernetes манифестов
    for (const manifest of manifests) {
      if (!manifest.apiVersion || !manifest.kind || !manifest.metadata?.name) {
        throw new Error(`Неверная структура Kubernetes манифеста: отсутствуют обязательные поля apiVersion, kind или metadata.name`);
      }
    }
    
    return manifests;
  } catch (error) {
    throw new Error(`Ошибка парсинга YAML: ${error instanceof Error ? error.message : 'Неизвестная ошибка'}`);
  }
}

export function getValueByPath(obj: any, path: string): any {
  if (!path) return obj;
  
  const parts = path.split('.');
  let current = obj;
  
  for (const part of parts) {
    // Обработка массивов, например containers[0]
    const arrayMatch = part.match(/^(.+)\[(\d+)\]$/);
    if (arrayMatch) {
      const [, key, index] = arrayMatch;
      current = current?.[key]?.[parseInt(index, 10)];
    } else {
      current = current?.[part];
    }
    
    if (current === undefined) {
      return undefined;
    }
  }
  
  return current;
}

export function setValueByPath(obj: any, path: string, value: any): void {
  if (!path) return;
  
  const parts = path.split('.');
  let current = obj;
  
  for (let i = 0; i < parts.length - 1; i++) {
    const part = parts[i];
    const arrayMatch = part.match(/^(.+)\[(\d+)\]$/);
    
    if (arrayMatch) {
      const [, key, index] = arrayMatch;
      const idx = parseInt(index, 10);
      
      if (!current[key]) {
        current[key] = [];
      }
      
      if (!current[key][idx]) {
        current[key][idx] = {};
      }
      
      current = current[key][idx];
    } else {
      if (!current[part]) {
        current[part] = {};
      }
      current = current[part];
    }
  }
  
  const lastPart = parts[parts.length - 1];
  const arrayMatch = lastPart.match(/^(.+)\[(\d+)\]$/);
  
  if (arrayMatch) {
    const [, key, index] = arrayMatch;
    const idx = parseInt(index, 10);
    
    if (!current[key]) {
      current[key] = [];
    }
    
    current[key][idx] = value;
  } else {
    current[lastPart] = value;
  }
}

export function deepClone<T>(obj: T): T {
  if (obj === null || typeof obj !== 'object') {
    return obj;
  }
  
  if (Array.isArray(obj)) {
    return obj.map(item => deepClone(item)) as unknown as T;
  }
  
  const cloned = {} as T;
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      cloned[key] = deepClone(obj[key]);
    }
  }
  
  return cloned;
}

export function isYamlValid(yamlString: string): boolean {
  try {
    yaml.load(yamlString);
    return true;
  } catch {
    return false;
  }
}
